import { authService } from "./authService";
import { BASE_URL } from "../config/api";
import { timezoneUtils } from "../utils/timezoneUtils";

export type MeditationType =
  | "pre-defined"
  | "customised"
  | "recorded"
  | "saved";
export type PredefinedTopic =
  | "awareness"
  | "i_am"
  | "the_body"
  | "time_and_space"
  | "love"
  | "the_witness"
  | "being"
  | "perception"
  | "peace_and_happiness"
  | "customised";
export type BackgroundSound = "none" | "strings" | "piano" | "flute";
export type SilenceDuration = "short" | "medium" | "long";

export interface MeditationConfig {
  type: MeditationType;
  topic?: PredefinedTopic;
  customText?: string;
  duration: number;
  musicType: BackgroundSound | null;
  language: string;
  silenceDuration: SilenceDuration;
}

export interface MeditationGenerateResponse {
  hash: string;
  meta: {
    status: string;
  };
}

export interface MeditationStatus {
  status: "pending" | "processing" | "complete" | "error";
  meta?: {
    progress: number;
    status: string;
  };
  progress?: string;
  audio_url?: string;
  title?: string;
  error?: string;
  transcript?: string;
}

export interface MeditationPlayedStatus {
  played: boolean;
  playedAt: string | null;
}

export interface DailyMeditationSummary {
  hash: string;
  title: string;
  duration?: number;
  reference?: string;
  author?: string;
  type: "recorded" | "generated";
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface MeditationResponse {
  id: string;
  hash: string;
  title: string;
  description: string;
  duration: number;
  category?: string;
  createdAt: string;
  recordedAt?: string;
  playedAt?: string;
  scheduledDay?: string | null;
  audioUrl?: string;
  transcript?: string;
  type: "generated" | "recorded";
  musicType?: BackgroundSound;
  language?: string;
  customText?: string;
  sourceUrl?: string;
  playCount?: number;
  position?: number; // For series items
  reference?: string; // Reference identifier (e.g., I001, I002 for introductory series)
  author?: string; // Author of the meditation
  isSaved?: boolean; // Whether the meditation has been saved by the user
  introEndTime?: number; // Timestamp in seconds where intro ends and main content begins
}

export interface MeditationPreferences {
  type: "recorded" | "generated";
  duration?: number;
  topic?: string;
  notification_enabled?: boolean;
  notification_time?: string;
  library_start_date?: string | null;
  lastGeneratedMeditation?: {
    hash?: string;
    language?: string;
    duration?: number;
    musicType?: string;
    custom_text?: string;
    topic?: string;
    silence_duration?: string;
  } | null;
}

export interface MeditationSeries {
  id: number;
  title: string;
  description: string;
  created_at: string;
  updated_at: string;
  meditation_count: number;
  total_duration: number;
  meditations?: MeditationResponse[];
}

export interface MeditationSeriesInfo {
  in_series: boolean;
  series?: {
    id: number;
    title: string;
    total_meditations: number;
    current_position: number;
  };
  navigation?: {
    previous: {
      hash: string;
      title: string;
      position: number;
    } | null;
    next: {
      hash: string;
      title: string;
      position: number;
    } | null;
  };
}

const defaultHeaders = {
  "Content-Type": "application/json",
};

const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
  const token = authService.getToken();
  const headers = {
    ...defaultHeaders,
    ...options.headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  try {
    const response = await fetch(`${BASE_URL}${url}`, {
      ...options,
      headers,
      mode: "cors",
      // Removed credentials: 'include' to avoid CORS issues - using Bearer token auth
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        // Clear current auth state
        await authService.logout();

        // Save the current URL to redirect back after login
        const currentPath = window.location.pathname + window.location.search;
        if (!currentPath.includes("/login")) {
          sessionStorage.setItem("redirectAfterLogin", currentPath);
          localStorage.setItem("redirectAfterLogin", currentPath);
        }

        // Force reload to login page
        window.location.replace("/login?expired=true");
        throw new Error("Session expired");
      }

      try {
        const error = await response.json();
        throw new Error(
          error.message || `HTTP error! status: ${response.status}`
        );
      } catch (e) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    }

    return response;
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      // Rethrow abort errors as is
      throw error;
    }
    if (error instanceof Error && error.message === "Session expired") {
      // Rethrow session expired errors as is
      throw error;
    }
    // Throw other errors with more context
    throw new Error(`Failed to fetch ${url}: ${error}`);
  }
};

interface MeditationService {
  universalSearch(query: string): Promise<MeditationResponse[]>;
  semanticSearch(
    query: string,
    category?: string,
    limit?: number,
    language?: string
  ): Promise<MeditationResponse[]>;
  generateMeditation(
    config: MeditationConfig
  ): Promise<MeditationGenerateResponse>;
  trackPlay(hash: string): Promise<void>;
  trackStarted(hash: string): Promise<void>;
  saveMeditation(hash: string): Promise<boolean>;
  isMeditationSaved(hash: string): Promise<boolean>;
  getMeditationPlayedStatus(
    hash: string,
    signal?: AbortSignal
  ): Promise<MeditationPlayedStatus>;
  unsaveMeditation(hash: string): Promise<boolean>;
  getMeditationByHash(hash: string): Promise<MeditationResponse>;
  getMeditationByUUID(uuid: string): Promise<MeditationResponse>;
  getMeditationStatus(hash: string): Promise<MeditationStatus>;
  getRecordedMeditations(
    category?: string,
    signal?: AbortSignal,
    sortByName?: boolean,
    page?: number,
    pageSize?: number,
    dailyScheduleOnly?: boolean
  ): Promise<PaginatedResponse<MeditationResponse>>;
  getSavedMeditations(
    signal?: AbortSignal,
    page?: number,
    pageSize?: number
  ): Promise<PaginatedResponse<MeditationResponse>>;
  getMeditationHistory(
    signal?: AbortSignal,
    page?: number,
    pageSize?: number
  ): Promise<PaginatedResponse<MeditationResponse>>;
  getGeneratedMeditations(
    signal?: AbortSignal,
    page?: number,
    pageSize?: number
  ): Promise<PaginatedResponse<MeditationResponse>>;
  retryMeditation(hash: string): Promise<MeditationGenerateResponse>;
  submitFeedback(
    meditationId: string,
    feedback: { rating: number; comment?: string }
  ): Promise<void>;
  getMeditationPromptFromChat(): Promise<{ prompt: string; context: string }>;
  getDailyMeditationHash(): Promise<string>;
  getDailyMeditationSummary(): Promise<DailyMeditationSummary>;
  getDailyMeditationPreferences(): Promise<MeditationPreferences>;
  updateDailyMeditationPreferences(
    preferences: MeditationPreferences
  ): Promise<void>;
  updateDailyPreferences(preferences: MeditationPreferences): Promise<void>;
  updateLibrarySettings(libraryStartDate: string | null): Promise<void>;
  getAdminDashboard(): Promise<any>;
  universalSearch(query: string): Promise<MeditationResponse[]>;
  semanticSearch(
    query: string,
    category?: string,
    limit?: number,
    language?: string
  ): Promise<MeditationResponse[]>;
  generateMeditation(
    config: MeditationConfig
  ): Promise<MeditationGenerateResponse>;
  trackPlay(hash: string): Promise<void>;
  saveMeditation(hash: string): Promise<boolean>;
  // Meditation Series methods
  getAllMeditationSeries(signal?: AbortSignal): Promise<MeditationSeries[]>;
  getMeditationSeriesById(
    seriesId: number,
    signal?: AbortSignal
  ): Promise<MeditationSeries>;
  isMeditationSaved(hash: string): Promise<boolean>;
  getMeditationPlayedStatus(
    hash: string,
    signal?: AbortSignal
  ): Promise<MeditationPlayedStatus>;
  unsaveMeditation(hash: string): Promise<boolean>;
  getMeditationByHash(hash: string): Promise<MeditationResponse>;
  getMeditationByUUID(uuid: string): Promise<MeditationResponse>;
  getMeditationStatus(hash: string): Promise<MeditationStatus>;

  retryMeditation(hash: string): Promise<MeditationGenerateResponse>;
  submitFeedback(
    meditationId: string,
    feedback: { rating: number; comment?: string }
  ): Promise<void>;
  getMeditationPromptFromChat(): Promise<{ prompt: string; context: string }>;
  getDailyMeditationHash(): Promise<string>;
  getDailyMeditationSummary(): Promise<DailyMeditationSummary>;
}

class MeditationServiceImpl implements MeditationService {
  /**
   * Requests a translation for a meditation by hash and target language
   * @param hash - The unique hash identifier of the meditation
   * @param targetLanguage - The target language for translation
   * @returns Promise resolving to the translated meditation or pending status
   */
  async getMeditationTranslation(
    hash: string,
    targetLanguage: string
  ): Promise<any> {
    const url = `/api/meditations/${hash}/translation`;
    const response = await fetchWithAuth(url, {
      method: "POST",
      headers: defaultHeaders,
      body: JSON.stringify({ target_language: targetLanguage }),
    });
    return await response.json();
  }

  // Meditation Series methods
  async getAllMeditationSeries(
    signal?: AbortSignal
  ): Promise<MeditationSeries[]> {
    const response = await fetchWithAuth("/api/meditation-series/", {
      method: "GET",
      signal,
    });
    const data = await response.json();
    return data.series || [];
  }

  async getMeditationSeriesById(
    seriesId: number,
    signal?: AbortSignal
  ): Promise<MeditationSeries> {
    const response = await fetchWithAuth(`/api/meditation-series/${seriesId}`, {
      method: "GET",
      signal,
    });
    return await response.json();
  }

  async getMeditationSeriesInfo(
    meditationHash: string,
    signal?: AbortSignal
  ): Promise<MeditationSeriesInfo> {
    const response = await fetchWithAuth(
      `/api/meditation-series/meditation/${meditationHash}/series-info`,
      {
        method: "GET",
        signal,
      }
    );
    return await response.json();
  }
  /**
   * Performs a universal search across all meditations
   *
   * @param query - The search query string
   * @returns Promise resolving to an array of meditation responses matching the query
   */
  async universalSearch(query: string): Promise<MeditationResponse[]> {
    const url = `/api/search/universal_search`;
    const response = await fetchWithAuth(url, {
      method: "POST",
      headers: defaultHeaders,
      body: JSON.stringify({ query }),
    });

    if (!response.ok) {
      throw new Error(
        `Failed to perform intent search: ${response.status} ${response.statusText}`
      );
    }

    try {
      const data = await response.json();
      if (!Array.isArray(data)) {
        console.error("Search response is not an array:", data);
        throw new Error("Unexpected response format from search");
      }
      return data;
    } catch (error) {
      debugger;
      console.error("Error parsing search results:", error);
      throw new Error("Failed to parse search results");
    }
  }

  /**
   * Generates a new meditation based on provided configuration
   *
   * @param config - Configuration object containing meditation parameters like topic, duration, etc.
   * @returns Promise resolving to a response containing the meditation hash and status
   */
  async generateMeditation(
    config: MeditationConfig
  ): Promise<MeditationGenerateResponse> {
    try {
      const response = await fetchWithAuth("/api/meditations/generate", {
        method: "POST",
        body: JSON.stringify(config),
      });
      return response.json();
    } catch (error) {
      console.error("Error generating meditation:", error);
      throw error;
    }
  }

  /**
   * Tracks when a user plays a meditation for analytics and history
   *
   * @param hash - The hash of the meditation being played
   * @returns Promise that resolves when tracking is complete
   */
  async trackPlay(hash: string): Promise<void> {
    try {
      await fetchWithAuth("/api/meditations/play", {
        method: "POST",
        body: JSON.stringify({ hash }),
      });
    } catch (error) {
      console.error("Error tracking meditation play:", error);
      throw error;
    }
  }

  /**
   * Tracks when a user starts playing a meditation on the frontend
   *
   * @param hash - The hash of the meditation being started
   * @returns Promise that resolves when tracking is complete
   */
  async trackStarted(hash: string): Promise<void> {
    try {
      await fetchWithAuth("/api/meditations/started", {
        method: "POST",
        body: JSON.stringify({ hash }),
      });
    } catch (error) {
      console.error("Error tracking meditation start:", error);
      // Don't throw error to avoid interrupting playback
    }
  }

  /**
   * Saves a meditation to the user's favorites list
   *
   * @param hash - The unique hash identifier of the meditation to save
   * @returns Promise resolving to a boolean indicating success
   */
  async saveMeditation(hash: string): Promise<boolean> {
    try {
      const response = await fetchWithAuth(`/api/meditations/${hash}/save`, {
        method: "POST",
      });
      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error("Error saving meditation:", error);
      throw error;
    }
  }

  /**
   * Checks if a meditation is already saved in the user's favorites
   *
   * @param hash - The unique hash identifier of the meditation to check
   * @returns Promise resolving to a boolean indicating if the meditation is saved
   */
  async isMeditationSaved(hash: string): Promise<boolean> {
    try {
      const response = await fetchWithAuth(`/api/meditations/${hash}/saved`);
      const data = await response.json();
      return data.saved;
    } catch (error) {
      console.error("Error checking if meditation is saved:", error);
      throw error;
    }
  }

  async getMeditationPlayedStatus(
    hash: string,
    signal?: AbortSignal
  ): Promise<MeditationPlayedStatus> {
    try {
      const response = await fetchWithAuth(`/api/meditations/${hash}/played`, {
        method: "GET",
        signal,
      });
      return response.json();
    } catch (error) {
      console.error("Error checking if meditation is played:", error);
      throw error;
    }
  }

  /**
   * Removes a meditation from the user's favorites list
   *
   * @param hash - The unique hash identifier of the meditation to remove
   * @returns Promise resolving to a boolean indicating success
   */
  async unsaveMeditation(hash: string): Promise<boolean> {
    try {
      const response = await fetchWithAuth(`/api/meditations/${hash}/save`, {
        method: "DELETE",
      });
      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error("Error unsaving meditation:", error);
      throw error;
    }
  }

  /**
   * Retrieves complete meditation details by its hash identifier
   *
   * @param hash - The unique hash identifier of the meditation
   * @returns Promise resolving to the complete meditation data
   */
  async getMeditationByHash(hash: string): Promise<MeditationResponse> {
    try {
      const response = await fetchWithAuth(`/api/meditations/${hash}`);
      const data = await response.json();

      // Transform snake_case to camelCase
      return {
        id: data.id,
        hash: data.hash,
        title: data.title,
        description: data.description || "",
        audioUrl: data.audio_url,
        duration: data.duration,
        transcript: data.transcript,
        musicType: data.music_type,
        customText: data.custom_text,
        createdAt: data.created_at || new Date().toISOString(),
        recordedAt: data.recorded_at,
        category: data.category,
        language: data.language,
        sourceUrl: data.source_url,
        reference: data.reference,
        author: data.author,
        introEndTime: data.intro_end_time,
        type: data.type || "generated",
      };
    } catch (error) {
      console.error("Error getting meditation:", error);
      throw error;
    }
  }

  async getMeditationByUUID(uuid: string): Promise<MeditationResponse> {
    try {
      const response = await fetchWithAuth(`/api/meditations/uuid/${uuid}`);
      return response.json();
    } catch (error) {
      console.error("Error getting meditation:", error);
      throw error;
    }
  }

  /**
   * Gets the current generation status of a meditation
   *
   * @param hash - The unique hash identifier of the meditation
   * @returns Promise resolving to the status including stage, progress, and completion
   */
  async getMeditationStatus(hash: string): Promise<MeditationStatus> {
    try {
      const response = await fetchWithAuth(`/api/meditations/status/${hash}`);
      return response.json();
    } catch (error) {
      console.error("Error fetching meditation status:", error);
      throw error;
    }
  }

  /**
   * Retrieves a paginated list of recorded (non-AI-generated) meditations
   *
   * @param category - Optional category filter for meditations
   * @param signal - Optional AbortSignal for cancelling the request
   * @param sortByName - Optional flag to sort results by name instead of date
   * @param page - Page number for pagination (default: 1)
   * @param pageSize - Number of items per page (default: 20)
   * @returns Promise resolving to paginated list of recorded meditations
   */
  async getRecordedMeditations(
    category?: string,
    signal?: AbortSignal,
    sortByName?: boolean,
    page: number = 1,
    pageSize: number = 20,
    dailyScheduleOnly: boolean = false
  ): Promise<PaginatedResponse<MeditationResponse>> {
    try {
      const params = new URLSearchParams();
      if (category) {
        params.append("category", category);
      }
      if (sortByName) {
        params.append("sort_by_name", "true");
      }
      if (dailyScheduleOnly) {
        params.append("daily_schedule", "true");
      }
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());
      const endpoint = `/api/meditations/recorded?${params.toString()}`;
      const response = await fetchWithAuth(endpoint, {
        signal,
      });
      return response.json();
    } catch (error) {
      console.error("Error fetching recorded meditations:", error);
      throw error;
    }
  }

  /**
   * Retrieves a paginated list of meditations saved by the current user
   *
   * @param signal - Optional AbortSignal for cancelling the request
   * @param page - Page number for pagination (default: 1)
   * @param pageSize - Number of items per page (default: 20)
   * @returns Promise resolving to paginated list of saved meditations
   */
  async getSavedMeditations(
    signal?: AbortSignal,
    page: number = 1,
    pageSize: number = 20
  ): Promise<PaginatedResponse<MeditationResponse>> {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());
      const response = await fetchWithAuth(
        `/api/meditations/saved?${params.toString()}`,
        {
          signal,
        }
      );
      return response.json();
    } catch (error) {
      console.error("Error fetching saved meditations:", error);
      throw error;
    }
  }

  /**
   * Retrieves a paginated list of the user's meditation history
   *
   * @param signal - Optional AbortSignal for cancelling the request
   * @param page - Page number for pagination (default: 1)
   * @param pageSize - Number of items per page (default: 20)
   * @returns Promise resolving to paginated list of meditation history
   */
  async getMeditationHistory(
    signal?: AbortSignal,
    page: number = 1,
    pageSize: number = 20
  ): Promise<PaginatedResponse<MeditationResponse>> {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    const response = await fetchWithAuth(
      `/api/meditations/history?${params.toString()}`,
      { signal }
    );
    return response.json();
  }

  /**
   * Retrieves a paginated list of AI-generated meditations
   *
   * @param signal - Optional AbortSignal for cancelling the request
   * @param page - Page number for pagination (default: 1)
   * @param pageSize - Number of items per page (default: 20)
   * @returns Promise resolving to paginated list of generated meditations
   */
  async getGeneratedMeditations(
    signal?: AbortSignal,
    page: number = 1,
    pageSize: number = 20
  ): Promise<PaginatedResponse<MeditationResponse>> {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    const response = await fetch(
      `/api/meditations/generated?${params.toString()}`,
      { signal }
    );
    if (!response.ok) throw new Error("Failed to fetch generated meditations");
    return response.json();
  }

  /**
   * Retries the generation of a meditation that may have failed
   *
   * @param hash - The unique hash identifier of the meditation to retry
   * @returns Promise resolving to a response with the meditation generation status
   */
  async retryMeditation(hash: string): Promise<MeditationGenerateResponse> {
    const response = await fetchWithAuth(`/api/meditations/retry/${hash}`, {
      method: "POST",
    });
    return response.json();
  }

  /**
   * Submits user feedback for a meditation
   *
   * @param meditationId - The ID of the meditation being rated
   * @param feedback - Object containing rating (required) and optional comment
   * @returns Promise that resolves when feedback is submitted successfully
   */
  async submitFeedback(
    meditationId: string,
    feedback: { rating: number; comment?: string }
  ): Promise<void> {
    try {
      await fetchWithAuth(
        `/api/meditations/${meditationId}/feedback`,
        {
          method: "POST",
          body: JSON.stringify(feedback),
        }
      );
    } catch (error) {
      console.error("Error submitting feedback:", error);
      throw error;
    }
  }

  async getMeditationPromptFromChat(): Promise<{
    prompt: string;
    context: string;
  }> {
    const response = await fetch(
      `${BASE_URL}/api/meditations/prompt-from-chat`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authService.getToken()}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || "Failed to get meditation prompt");
    }

    return response.json();
  }

  /**
   * Gets the hash of the user's daily meditation
   *
   * @returns Promise resolving to the hash string of the daily meditation
   */
  async getDailyMeditationHash(): Promise<string> {
    try {
      const data = await this.getDailyMeditationSummary();
      return data.hash;
    } catch (error) {
      console.error("Error fetching daily meditation:", error);
      throw error;
    }
  }

  async getDailyMeditationSummary(): Promise<DailyMeditationSummary> {
    try {
      const response = await fetchWithAuth("/api/meditations/daily");
      if (!response.ok) {
        throw response;
      }
      return response.json();
    } catch (error) {
      console.error("Error fetching daily meditation summary:", error);
      throw error;
    }
  }

  /**
   * Retrieves the user's preferences for daily meditations
   *
   * @returns Promise resolving to the user's meditation preferences
   */
  async getDailyMeditationPreferences(): Promise<MeditationPreferences> {
    const response = await fetchWithAuth("/api/meditations/daily-preferences");
    const data = await response.json();

    const basePreferences = {
      type: data.type,
      library_start_date: data.library_start_date,
      notification_enabled: data.notification_enabled,
      notification_time: data.notification_time
        ? timezoneUtils.utcToLocal(data.notification_time)
        : undefined,
    };

    if (data.type === "generated") {
      return {
        ...basePreferences,
        lastGeneratedMeditation: {
          hash: data.hash,
          language: data.language,
          duration: data.duration,
          musicType: data.music_type,
          custom_text: data.custom_text,
          topic: data.topic,
          silence_duration: data.silence_duration,
        },
      };
    }

    return {
      ...basePreferences,
      duration: data.duration,
    };
  }

  async updateDailyMeditationPreferences(
    preferences: MeditationPreferences
  ): Promise<void> {
    try {
      // Filter out null or undefined values
      const filteredPreferences = {
        type: preferences.type,
        ...(preferences.duration !== undefined && {
          duration: preferences.duration,
        }),
        ...(preferences.lastGeneratedMeditation &&
          preferences.type === "generated" && {
            lastGeneratedMeditation: {
              hash: preferences.lastGeneratedMeditation.hash,
              language: preferences.lastGeneratedMeditation.language,
              duration: preferences.lastGeneratedMeditation.duration,
              musicType: preferences.lastGeneratedMeditation.musicType,
              custom_text: preferences.lastGeneratedMeditation.custom_text,
              topic: preferences.lastGeneratedMeditation.topic,
              silence_duration:
                preferences.lastGeneratedMeditation.silence_duration,
            },
          }),
      };

      await fetchWithAuth("/api/meditations/daily-preferences", {
        method: "POST",
        body: JSON.stringify(filteredPreferences),
      });
    } catch (error) {
      console.error("Error updating daily meditation preferences:", error);
      throw error;
    }
  }

  async updateDailyPreferences(
    preferences: MeditationPreferences
  ): Promise<void> {
    try {
      const localTime = preferences.notification_time || "08:00";
      const utcTime = timezoneUtils.localToUtc(localTime);

      const updatedPreferences = {
        ...preferences,
        notification_time: utcTime,
      };

      await fetchWithAuth("/api/meditations/daily-preferences", {
        method: "POST",
        body: JSON.stringify(updatedPreferences),
      });
    } catch (error) {
      console.error("Error updating daily preferences:", error);
      throw error;
    }
  }

  async updateLibrarySettings(libraryStartDate: string | null): Promise<void> {
    try {
      await fetchWithAuth("/api/meditations/library-settings", {
        method: "POST",
        body: JSON.stringify({ library_start_date: libraryStartDate }),
      });
    } catch (error) {
      console.error("Error updating library settings:", error);
      throw error;
    }
  }

  async getAdminDashboard(): Promise<any> {
    try {
      // Get token - try multiple methods to ensure we have it
      let token = authService.getToken();

      // If token is not available, try to get current user which might refresh it
      if (!token) {
        try {
          const currentUser = await authService.fetchCurrentUser();
          if (currentUser) {
            token = authService.getToken();
          }
        } catch (e) {
          console.error("Failed to fetch current user:", e);
        }
      }

      if (!token) {
        console.error("No token available for admin dashboard request");
        throw new Error("Authentication required. Please log in again.");
      }

      console.log(
        "Fetching admin dashboard with token:",
        token.substring(0, 20) + "..."
      );

      const response = await fetch(`${BASE_URL}/api/admin-dashboard/`, {
        method: "GET",
        headers: {
          Accept: "application/json, text/plain, */*",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        mode: "cors",
        credentials: "omit", // Don't send cookies, use Bearer token only
      });

      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }

        if (response.status === 401 || response.status === 403) {
          await authService.logout();
          const currentPath = window.location.pathname + window.location.search;
          if (!currentPath.includes("/login")) {
            sessionStorage.setItem("redirectAfterLogin", currentPath);
          }
          window.location.replace("/login?expired=true");
          throw new Error("Session expired. Please log in again.");
        }

        // If token is missing error, provide helpful message
        if (
          errorMessage.includes("Token is missing") ||
          errorMessage.includes("token")
        ) {
          throw new Error(
            "Authentication token is missing. Please log out and log in again."
          );
        }

        throw new Error(errorMessage);
      }

      // Check content type to handle both JSON and HTML responses
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const data = await response.json();
        return data;
      } else {
        // If HTML is returned, we'll need to parse it or return it as is
        const html = await response.text();
        // Try to extract JSON from HTML if it's embedded, otherwise return HTML
        return { html, isHtml: true };
      }
    } catch (error) {
      console.error("Error fetching admin dashboard:", error);
      console.error("Error details:", {
        message: error instanceof Error ? error.message : String(error),
        name: error instanceof Error ? error.name : "Unknown",
        stack: error instanceof Error ? error.stack : undefined,
      });

      // If it's a network/CORS error, provide helpful message
      if (
        error instanceof TypeError &&
        error.message.includes("Failed to fetch")
      ) {
        throw new Error(
          "Unable to connect to the dashboard server. This might be a CORS issue. Please check if the API server allows requests from your domain."
        );
      }

      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Failed to fetch admin dashboard: ${error}`);
    }
  }

  async semanticSearch(
    query: string,
    category?: string,
    limit: number = 5,
    language: string = "english"
  ): Promise<MeditationResponse[]> {
    const response = await fetchWithAuth("/api/meditations/search", {
      method: "POST",
      headers: defaultHeaders,
      body: JSON.stringify({ query, category, limit, language }),
    });
    const data = await response.json();
    return data.results;
  }
}

export const meditationService = new MeditationServiceImpl();
